import { useI18n } from '@ecom/i18n';
import type { ReactNode } from 'react';

import { cx } from '../utils/cx';

export interface StepItem {
  readonly id: string;
  readonly label: string;
}

export interface StepperProps {
  readonly steps: readonly StepItem[];
  /** 0-based index of the step in progress. */
  readonly activeIndex: number;
}

/**
 * Linear progress indicator (§7.1 primitives), reproducing the storefront
 * prototype's checkout stepper: 1.75rem numbered dot, `--ecom-surface-2` when
 * upcoming, filled `--ecom-accent` when active, `--ecom-accent-soft` with an
 * accent border once done, joined by a 1.5px rule that turns accent-coloured
 * behind completed steps.
 *
 * Marked up as an ordered list inside a `<nav>`: the steps are a sequence,
 * and `<ol>` conveys both position and total to assistive technology for
 * free. The active step carries `aria-current="step"`.
 *
 * Each step's state is also spelled out as visually-hidden text. The dot
 * alone encodes state through colour and a checkmark, and neither survives
 * being read aloud — without this a screen-reader user hears "1 Panier, 2
 * Livraison, 3 Paiement" with no indication of what is finished.
 */
export function Stepper({ steps, activeIndex }: StepperProps): ReactNode {
  const { t } = useI18n();

  return (
    <nav className="ecom-stepper" aria-label={t('stepper.label')}>
      <ol className="ecom-stepper__list">
        {steps.map((step, index) => {
          const isDone = index < activeIndex;
          const isActive = index === activeIndex;
          const stateLabel = isDone
            ? t('stepper.completed')
            : isActive
              ? t('stepper.current')
              : t('stepper.upcoming');

          return (
            <li
              key={step.id}
              className={cx('ecom-stepper__step', isDone && 'is-done', isActive && 'is-active')}
              {...(isActive ? { 'aria-current': 'step' } : {})}
            >
              <span className="ecom-stepper__dot" aria-hidden="true">
                {isDone ? <CheckIcon /> : index + 1}
              </span>
              <span className="ecom-stepper__label">{step.label}</span>
              <span className="ecom-sr-only">{stateLabel}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function CheckIcon(): ReactNode {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M5 13l4 4L19 7"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
